import { useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { api, errMsg } from "./api";

export const BUILDING_TYPES = ["Commercial", "Residential", "Institutional", "High-Rise", "Hospitality", "Healthcare", "Industrial"];
export const WIND_SPEEDS = [33, 39, 44, 47, 50, 55];
const EMPTY = { name: "", client: "", location: "", building_type: "Commercial", basic_wind_speed: 44, building_height_m: 30, terrain_category: 2 };

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">{label}</Label>
    {children}
  </div>
);

const SelectField = ({ value, onChange, options, testid }) => (
  <Select value={String(value)} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="border-zinc-800 bg-zinc-900"><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const NewProjectDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Project name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/projects", { ...form, basic_wind_speed: Number(form.basic_wind_speed), building_height_m: Number(form.building_height_m), terrain_category: Number(form.terrain_category) });
      toast.success("Project created");
      setOpen(false);
      setForm(EMPTY);
      onCreated(data);
    } catch (err) {
      toast.error(errMsg(err, "Could not create project"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-project-btn" className="bg-sky-500 text-white hover:bg-sky-600"><Plus className="mr-2 h-4 w-4" />New Project</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg border-zinc-800 bg-[#121215]">
        <DialogHeader>
          <DialogTitle className="font-[Manrope]">New facade project</DialogTitle>
          <DialogDescription>The location and wind data feed every design check.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Project name"><Input data-testid="project-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} className="border-zinc-800 bg-zinc-900" /></Field></div>
          <Field label="Client"><Input data-testid="project-client-input" value={form.client} onChange={(e) => set("client")(e.target.value)} className="border-zinc-800 bg-zinc-900" /></Field>
          <Field label="Location"><Input data-testid="project-location-input" value={form.location} onChange={(e) => set("location")(e.target.value)} className="border-zinc-800 bg-zinc-900" /></Field>
          <Field label="Building type"><SelectField testid="project-building-type-select" value={form.building_type} onChange={set("building_type")} options={BUILDING_TYPES.map((b) => ({ value: b, label: b }))} /></Field>
          <Field label="Basic wind speed Vb"><SelectField testid="project-wind-speed-select" value={form.basic_wind_speed} onChange={set("basic_wind_speed")} options={WIND_SPEEDS.map((v) => ({ value: v, label: `${v} m/s` }))} /></Field>
          <Field label="Building height (m)"><Input data-testid="project-height-input" type="number" min="1" value={form.building_height_m} onChange={(e) => set("building_height_m")(e.target.value)} className="border-zinc-800 bg-zinc-900 font-mono" /></Field>
          <Field label="Terrain category"><SelectField testid="project-terrain-select" value={form.terrain_category} onChange={set("terrain_category")} options={[1, 2, 3, 4].map((v) => ({ value: v, label: `Category ${v}` }))} /></Field>
          <Button type="submit" disabled={saving} data-testid="create-project-submit-btn" className="col-span-2 mt-2 bg-sky-500 text-white hover:bg-sky-600">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create project
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
